<script setup>
// NOTICE: KAPTION-INTEGRATION
// OAuth consent page for the Rumo MCP. The MCP worker's /authorize redirects
// here with an opaque signed `oreq` blob (HMAC-verified by the worker, only
// decoded here for display). The user picks a workspace + permissions,
// confirms a WhatsApp 2FA code, and the browser is redirected back to the
// worker's /authorize/complete with a short-lived grant — completing the
// standard OAuth code flow. Account-less route: see routeHelpers.js.
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import kaptionWhatsapp2fa from 'dashboard/api/kaptionWhatsapp2fa';
import kaptionMcpGrant from 'dashboard/api/kaptionMcpGrant';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();

const currentUser = computed(() => store.getters.getCurrentUser);
const accounts = computed(() => currentUser.value?.accounts || []);

const oreq = computed(() => String(route.query.oreq || ''));

// Display-only decode of the blob payload ({i: oauthReqInfo, c: clientName}).
// Integrity is the worker's job — it re-verifies the HMAC on completion.
const clientName = computed(() => {
  try {
    const payload = oreq.value.split('.')[0];
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const decoded = JSON.parse(atob(padded));
    return decoded.c || decoded.i?.clientId || 'MCP client';
  } catch (e) {
    return '';
  }
});

const loading = ref(true);
const invalidRequest = ref(false);
const twoFaStatus = ref({ verified: false, phone: null });
const scopesCatalog = ref({});
const authorizeCompleteUrl = ref('');
const step = ref('consent'); // consent | code | redirecting
const selectedAccountId = ref(null);
// Multi-account: the connection covers the checked accounts, or every account
// the person belongs to when allAccounts is on (membership re-checked live).
const selectedAccountIds = ref(new Set());
const allAccounts = ref(false);
const accountsCatalog = ref([]);
// Roles are plain-language bundles; the scope matrix stays behind a disclosure
// for people who want the detail. Effective scopes = manual picks ∪ role bundles.
const rolesCatalog = ref({});
const selectedRoles = ref(new Set());
const showScopes = ref(false);
const selectedScopes = ref(new Set());
const codeInput = ref('');
const busy = ref(false);
const cooldownLeft = ref(0);
let cooldownTimer = null;

const errorMessage = e =>
  e?.response?.data?.error || t('KAPTION_MCP_CONSENT.GENERIC_ERROR');

const startCooldown = () => {
  cooldownLeft.value = 60;
  clearInterval(cooldownTimer);
  cooldownTimer = setInterval(() => {
    cooldownLeft.value -= 1;
    if (cooldownLeft.value <= 0) clearInterval(cooldownTimer);
  }, 1000);
};

onMounted(async () => {
  if (!oreq.value || !clientName.value) {
    invalidRequest.value = true;
    loading.value = false;
    return;
  }
  try {
    const { data } = await kaptionWhatsapp2fa.getStatus();
    twoFaStatus.value = data;
    if (data.verified) {
      const scopesRes = await kaptionMcpGrant.getScopes();
      scopesCatalog.value = scopesRes.data.available_scopes || {};
      rolesCatalog.value = scopesRes.data.roles || {};
      accountsCatalog.value = scopesRes.data.accounts || [];
      authorizeCompleteUrl.value = scopesRes.data.authorize_complete_url;
    }
    if (accounts.value.length === 1) {
      selectedAccountId.value = accounts.value[0].id;
      selectedAccountIds.value = new Set([accounts.value[0].id]);
    }
  } catch (e) {
    // 403 feature_disabled / whatsapp_2fa_required → show the 2FA prompt
  } finally {
    loading.value = false;
  }
});

onBeforeUnmount(() => clearInterval(cooldownTimer));

const scopeRows = computed(() =>
  Object.entries(scopesCatalog.value).map(([key, entry]) => ({
    key,
    label: entry.label,
    perms: ['read', 'write', 'delete']
      .map(kind => ({ kind, scope: entry[kind] }))
      .filter(p => p.scope),
  }))
);

const permLabel = kind => {
  if (kind === 'read') return t('KAPTION_MCP_CONSENT.PERM.READ');
  if (kind === 'write') return t('KAPTION_MCP_CONSENT.PERM.WRITE');
  return t('KAPTION_MCP_CONSENT.PERM.DELETE');
};

const accountLabel = account => `${account.name} (${account.role})`;

const pickableAccounts = computed(() =>
  accountsCatalog.value.length ? accountsCatalog.value : accounts.value
);

const toggleAccount = id => {
  const next = new Set(selectedAccountIds.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  selectedAccountIds.value = next;
};

const roleRows = computed(() =>
  Object.entries(rolesCatalog.value).map(([key, role]) => ({
    key,
    label: role.label,
    description: role.description,
    scopes: role.scopes || [],
  }))
);

const toggleRole = key => {
  const next = new Set(selectedRoles.value);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  selectedRoles.value = next;
};

const effectiveScopes = computed(() => {
  const out = new Set(selectedScopes.value);
  selectedRoles.value.forEach(key => {
    (rolesCatalog.value[key]?.scopes || []).forEach(scope => out.add(scope));
  });
  return out;
});

const hasAccountChoice = computed(
  () => allAccounts.value || selectedAccountIds.value.size > 0
);

const primaryAccountId = computed(() => {
  if (allAccounts.value) return pickableAccounts.value[0]?.id ?? null;
  return [...selectedAccountIds.value][0] ?? null;
});

const toggleScope = scope => {
  const next = new Set(selectedScopes.value);
  if (next.has(scope)) next.delete(scope);
  else next.add(scope);
  selectedScopes.value = next;
};

// Explicit per-entity scopes (not the 'read' meta-scope) so every selection
// is visible in the permission matrix and the grant stays minimal.
const selectRecommended = () => {
  // Keep this in step with TOOL_SCOPES in the Rumo MCP worker: a scope missing
  // here is a tool the user cannot call after accepting the recommendation.
  // inboxes/agents/teams/custom_filters reads were absent, which left
  // rumo_list_inboxes unusable — and campaign and template creation both need
  // an inbox id it returns.
  selectedScopes.value = new Set([
    'inboxes:read',
    'conversations:read',
    'conversations:write',
    'messages:read',
    'messages:write',
    'contacts:read',
    'contacts:write',
    'agents:read',
    'teams:read',
    'labels:read',
    'custom_filters:read',
    'campaigns:read',
    'campaigns:write',
    'whatsapp_templates:read',
    'whatsapp_templates:write',
    'ai_studio:read',
    'ai_studio:write',
    'reports:read',
  ]);
};

const goToTwoFaSetup = () => {
  router.push({
    name: 'profile_settings_whatsapp_2fa',
    params: { accountId: currentUser.value.account_id },
  });
};

const requestCode = async () => {
  if (!hasAccountChoice.value || effectiveScopes.value.size === 0) {
    useAlert(t('KAPTION_MCP_CONSENT.PICK_FIRST'));
    return;
  }
  busy.value = true;
  try {
    await kaptionMcpGrant.requestCode();
    step.value = 'code';
    startCooldown();
  } catch (e) {
    useAlert(errorMessage(e));
  } finally {
    busy.value = false;
  }
};

const approve = async () => {
  busy.value = true;
  try {
    const { data } = await kaptionMcpGrant.createGrant({
      accountId: primaryAccountId.value,
      accountIds: allAccounts.value ? [] : [...selectedAccountIds.value],
      allAccounts: allAccounts.value,
      roles: [...selectedRoles.value],
      scopes: [...selectedScopes.value],
      code: codeInput.value,
      oreq: oreq.value,
      clientName: clientName.value,
    });
    step.value = 'redirecting';
    window.location.assign(data.redirect_uri);
  } catch (e) {
    useAlert(errorMessage(e));
    busy.value = false;
  }
};

const deny = () => {
  if (authorizeCompleteUrl.value) {
    window.location.assign(
      `${authorizeCompleteUrl.value}?oreq=${encodeURIComponent(oreq.value)}&error=access_denied`
    );
  } else {
    window.close();
  }
};
</script>

<template>
  <div
    class="flex items-start justify-center w-full min-h-screen bg-n-background pt-[10vh] px-4"
  >
    <div class="w-full max-w-lg">
      <div class="bg-n-solid-1 rounded-xl border border-n-weak p-6 shadow-sm">
        <div v-if="loading" class="text-sm text-n-slate-11">
          {{ $t('KAPTION_MCP_CONSENT.LOADING') }}
        </div>

        <div v-else-if="invalidRequest" class="text-sm text-n-slate-11">
          {{ $t('KAPTION_MCP_CONSENT.INVALID_REQUEST') }}
        </div>

        <template v-else>
          <div class="flex items-center gap-3">
            <span
              class="flex items-center justify-center size-10 rounded-full bg-n-alpha-black2 flex-shrink-0"
            >
              <Icon icon="i-lucide-plug-zap" class="size-5 text-n-slate-11" />
            </span>
            <div class="min-w-0">
              <h1 class="text-base font-semibold text-n-slate-12 truncate">
                {{ $t('KAPTION_MCP_CONSENT.TITLE', { client: clientName }) }}
              </h1>
              <p class="text-sm text-n-slate-11">
                {{ $t('KAPTION_MCP_CONSENT.SUBTITLE', { client: clientName }) }}
              </p>
            </div>
          </div>

          <!-- 2FA prerequisite -->
          <div
            v-if="!twoFaStatus.verified"
            class="mt-5 p-4 rounded-lg bg-n-alpha-black2 border border-n-weak"
          >
            <div class="flex items-center gap-2">
              <Icon
                icon="i-lucide-shield-alert"
                class="size-4 text-n-amber-11 flex-shrink-0"
              />
              <h5 class="text-sm font-semibold text-n-slate-12">
                {{ $t('KAPTION_MCP_CONSENT.TWO_FA_REQUIRED_TITLE') }}
              </h5>
            </div>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ $t('KAPTION_MCP_CONSENT.TWO_FA_REQUIRED_DESC') }}
            </p>
            <Button
              type="button"
              class="mt-3"
              :label="$t('KAPTION_MCP_CONSENT.TWO_FA_SETUP')"
              icon="i-lucide-shield-check"
              @click="goToTwoFaSetup"
            />
          </div>

          <!-- Consent: workspace + permissions -->
          <template v-else-if="step === 'consent'">
            <div class="mt-5 flex flex-col gap-2">
              <span class="text-sm text-n-slate-11">
                {{ $t('KAPTION_MCP_CONSENT.ACCOUNTS_LABEL') }}
              </span>
              <div
                class="border border-n-weak rounded-lg divide-y divide-n-weak max-h-48 overflow-y-auto"
              >
                <label
                  class="flex items-start gap-2 px-3 py-2 text-sm text-n-slate-12 cursor-pointer"
                >
                  <input
                    v-model="allAccounts"
                    type="checkbox"
                    class="mt-1"
                    data-testid="mcp-all-accounts"
                  />
                  <span class="flex flex-col">
                    <span>
                      {{
                        $t('KAPTION_MCP_CONSENT.ALL_ACCOUNTS', {
                          count: pickableAccounts.length,
                        })
                      }}
                    </span>
                    <span class="text-xs text-n-slate-10">
                      {{ $t('KAPTION_MCP_CONSENT.ALL_ACCOUNTS_HINT') }}
                    </span>
                  </span>
                </label>
                <label
                  v-for="account in pickableAccounts"
                  :key="account.id"
                  class="flex items-center gap-2 px-3 py-2 text-sm text-n-slate-12"
                  :class="allAccounts ? 'opacity-50' : 'cursor-pointer'"
                >
                  <input
                    type="checkbox"
                    :checked="allAccounts || selectedAccountIds.has(account.id)"
                    :disabled="allAccounts"
                    @change="toggleAccount(account.id)"
                  />
                  {{ accountLabel(account) }}
                </label>
              </div>
            </div>

            <div class="flex flex-col gap-2 mt-4">
              <span class="text-sm text-n-slate-11">
                {{
                  $t('KAPTION_MCP_CONSENT.ROLES_LABEL', { client: clientName })
                }}
              </span>
              <div
                class="border border-n-weak rounded-lg divide-y divide-n-weak"
              >
                <label
                  v-for="role in roleRows"
                  :key="role.key"
                  class="flex items-start gap-2 px-3 py-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    class="mt-1"
                    :checked="selectedRoles.has(role.key)"
                    :data-testid="`mcp-role-${role.key}`"
                    @change="toggleRole(role.key)"
                  />
                  <span class="flex flex-col">
                    <span class="text-sm text-n-slate-12">{{
                      role.label
                    }}</span>
                    <span class="text-xs text-n-slate-10">{{
                      role.description
                    }}</span>
                  </span>
                </label>
              </div>
            </div>

            <div class="flex items-center justify-between mt-4">
              <button
                type="button"
                class="text-xs text-n-slate-11 underline"
                data-testid="mcp-toggle-scopes"
                @click="showScopes = !showScopes"
              >
                {{
                  showScopes
                    ? $t('KAPTION_MCP_CONSENT.HIDE_SCOPES')
                    : $t('KAPTION_MCP_CONSENT.SHOW_SCOPES', {
                        count: effectiveScopes.size,
                      })
                }}
              </button>
              <Button
                v-if="showScopes"
                type="button"
                ghost
                sm
                :label="$t('KAPTION_MCP_CONSENT.RECOMMENDED')"
                @click="selectRecommended"
              />
            </div>
            <div
              v-if="showScopes"
              class="mt-2 border border-n-weak rounded-lg divide-y divide-n-weak max-h-64 overflow-y-auto"
            >
              <div
                v-for="row in scopeRows"
                :key="row.key"
                class="flex items-center justify-between px-3 py-2"
              >
                <span class="text-sm text-n-slate-12">{{ row.label }}</span>
                <div class="flex gap-4">
                  <label
                    v-for="perm in row.perms"
                    :key="perm.scope"
                    class="flex items-center gap-1.5 text-xs text-n-slate-11 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      :checked="effectiveScopes.has(perm.scope)"
                      :disabled="
                        !selectedScopes.has(perm.scope) &&
                        effectiveScopes.has(perm.scope)
                      "
                      @change="toggleScope(perm.scope)"
                    />
                    {{ permLabel(perm.kind) }}
                  </label>
                </div>
              </div>
            </div>
            <p class="mt-2 text-xs text-n-slate-10">
              {{ $t('KAPTION_MCP_CONSENT.RECONNECT_NOTE') }}
            </p>

            <div class="flex items-center justify-end gap-2 mt-5">
              <Button
                type="button"
                ghost
                :label="$t('KAPTION_MCP_CONSENT.DENY')"
                @click="deny"
              />
              <Button
                type="button"
                :label="$t('KAPTION_MCP_CONSENT.CONTINUE')"
                :disabled="busy || !hasAccountChoice || !effectiveScopes.size"
                @click="requestCode"
              />
            </div>
          </template>

          <!-- Fresh code confirmation -->
          <template v-else-if="step === 'code'">
            <p class="mt-5 text-sm text-n-slate-11">
              {{
                $t('KAPTION_MCP_CONSENT.CODE_SENT', {
                  phone: twoFaStatus.phone,
                })
              }}
            </p>
            <div class="flex items-center gap-2 mt-3">
              <input
                v-model="codeInput"
                type="text"
                inputmode="numeric"
                maxlength="6"
                :placeholder="$t('KAPTION_WA2FA.ENROLL.CODE_PLACEHOLDER')"
                class="flex-1 h-10 px-3 text-sm font-mono tracking-widest rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:border-n-brand outline-none"
              />
              <Button
                type="button"
                teal
                :label="$t('KAPTION_MCP_CONSENT.APPROVE')"
                :disabled="busy || codeInput.length !== 6"
                class="flex-shrink-0"
                @click="approve"
              />
            </div>
            <div class="flex items-center justify-between mt-4">
              <Button
                type="button"
                ghost
                sm
                :label="$t('KAPTION_MCP_CONSENT.BACK')"
                @click="step = 'consent'"
              />
              <Button
                type="button"
                ghost
                sm
                :label="$t('KAPTION_MCP_CONSENT.DENY')"
                @click="deny"
              />
            </div>
          </template>

          <!-- Redirecting back to the client -->
          <p v-else class="mt-5 text-sm text-n-slate-11">
            {{ $t('KAPTION_MCP_CONSENT.REDIRECTING', { client: clientName }) }}
          </p>
        </template>
      </div>
    </div>
  </div>
</template>
